import {
  Radio,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Text,
} from "@chakra-ui/react";
import { InvestorTransferSetupFooter } from "features/investor-transfer/setup/InvestorTransferSetupFooter";
import {
  useInvestorTransferComplete,
  useInvestorTransferorsForInvestorTransferListSuspense,
  useInvestorTransferRetrieveSuspense,
} from "gen/endpoints/investor-transfer/investor-transfer";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { FormRadioGroup } from "shared/components/radio/FormRadio";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";

interface TransferFinalizeFormData {
  action: "process_transfer" | "schedule_transfer" | "save_transfer";
}

export const InvestorTransferSetupFinalizePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["investorTransfer", "common"]);

  const { fundFamilyId, investorTransferId } = useRequiredStringParams();

  const form = useForm<TransferFinalizeFormData>();

  const action = useWatch({
    control: form.control,
    name: "action",
  });

  const continueButtonText = (() => {
    switch (action) {
      case "process_transfer":
        return t(
          "investorTransfer:setup.finalize.processTransfer.submitButton",
        );
      case "schedule_transfer":
        return t(
          "investorTransfer:setup.finalize.scheduleTransfer.submitButton",
        );
      case "save_transfer":
        return t("investorTransfer:setup.finalize.saveTransfer.submitButton");
    }
  })();

  const successMessage = (() => {
    switch (action) {
      case "process_transfer":
        return t(
          "investorTransfer:setup.finalize.processTransfer.successMessage",
        );
      case "schedule_transfer":
        return t(
          "investorTransfer:setup.finalize.scheduleTransfer.successMessage",
        );
      case "save_transfer":
        return t("investorTransfer:setup.finalize.saveTransfer.successMessage");
    }
  })();

  const { data } = useInvestorTransferRetrieveSuspense(
    fundFamilyId,
    investorTransferId,
  );
  const { data: investorTransferorDataList } =
    useInvestorTransferorsForInvestorTransferListSuspense(
      fundFamilyId,
      investorTransferId,
    );

  const { mutateAsync: completeInvestorTransfer } =
    useInvestorTransferComplete();

  const descriptionI18nParams = {
    name: investorTransferorDataList[0].investor_legal_name,
  };

  return (
    <PageContent contentWidth="boxed">
      <Form
        form={form}
        successMessage={successMessage}
        disableNavBlocker={true}
        onSubmit={async ({ action }) => {
          switch (action) {
            case "process_transfer":
              await completeInvestorTransfer({
                fundFamilyId,
                investorTransferId,
              });
              break;
            case "save_transfer":
              // Nothing to do here - transfers are saved by default.
              break;
            case "schedule_transfer":
              // TODO: Implement scheduled transfers
              break;
          }
        }}
        onSuccess={() => {
          navigate(
            rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .commitments({})
              .transfersTab({}).$,
          );
        }}
      >
        <PageHeader
          size="lg"
          workflowName={t("investorTransfer:setup.superHeading")}
          title={t("investorTransfer:setup.finalize.title")}
        />
        <PageBody>
          <FormRadioGroup name="action" variant="card">
            <Radio value="process_transfer">
              <Text textStyle="h5">
                {t("investorTransfer:setup.finalize.processTransfer.title")}
              </Text>
              <StatGroup>
                <Stat size="overline" flexGrow={0}>
                  <StatLabel>
                    {t("investorTransfer:setup.finalize.processDate")}
                  </StatLabel>
                  <StatNumber>
                    {formatIsoDateToDisplay(data.transfer_date)}
                  </StatNumber>
                </Stat>
                <Stat size="overline" flexGrow={1}>
                  <StatLabel>
                    {t("investorTransfer:setup.finalize.nextSteps")}
                  </StatLabel>
                  <StatNumber>
                    {t(
                      "investorTransfer:setup.finalize.processTransfer.description",
                      descriptionI18nParams,
                    )}
                  </StatNumber>
                </Stat>
              </StatGroup>
            </Radio>
            <Radio value="schedule_transfer" isDisabled={true}>
              <Text textStyle="h5">
                {t("investorTransfer:setup.finalize.scheduleTransfer.title")}
              </Text>
              <StatGroup>
                <Stat size="overline" flexGrow={0}>
                  <StatLabel>
                    {t("investorTransfer:setup.finalize.processDate")}
                  </StatLabel>
                  <StatNumber>
                    {t(
                      "investorTransfer:setup.finalize.scheduleTransfer.schedule",
                    )}
                  </StatNumber>
                </Stat>
                <Stat size="overline" flexGrow={1}>
                  <StatLabel>
                    {t("investorTransfer:setup.finalize.nextSteps")}
                  </StatLabel>
                  <StatNumber>
                    {t(
                      "investorTransfer:setup.finalize.scheduleTransfer.description",
                      descriptionI18nParams,
                    )}
                  </StatNumber>
                </Stat>
              </StatGroup>
            </Radio>
            <Radio value="save_transfer">
              <Text textStyle="h5">
                {t("investorTransfer:setup.finalize.saveTransfer.title")}
              </Text>
              <StatGroup>
                <Stat size="overline" flexGrow={0}>
                  <StatLabel>
                    {t("investorTransfer:setup.finalize.processDate")}
                  </StatLabel>
                  <StatNumber>{t("common:nA")}</StatNumber>
                </Stat>
                <Stat size="overline" flexGrow={1}>
                  <StatLabel>
                    {t("investorTransfer:setup.finalize.nextSteps")}
                  </StatLabel>
                  <StatNumber>
                    {t(
                      "investorTransfer:setup.finalize.saveTransfer.description",
                      descriptionI18nParams,
                    )}
                  </StatNumber>
                </Stat>
              </StatGroup>
            </Radio>
          </FormRadioGroup>
        </PageBody>
        <InvestorTransferSetupFooter
          fundFamilyId={fundFamilyId}
          allowBack={true}
          continueButtonText={continueButtonText}
          isDisabled={!action}
        />
      </Form>
    </PageContent>
  );
};
